import { useCallback, useEffect, useMemo, useRef } from 'react';
import { addUrlQueryParam, getUrlQueryParam } from '@/lib/url-helpers';
import { useSearch } from '@tanstack/react-router';

/**
 * Custom hook for managing tab state synchronized with query string URL parameters.
 * Provides an updateTabPath function that other components can use to update the tab path.
 * Uses TanStack Router's useSearch hook to reactively sync with URL changes.
 *
 * @param variant - The variant of the tabs (page or drawer)
 * @param onChange - Optional callback when the tab path changes
 * @returns Object with tabPath and updateTabPath function
 */

export enum TabVariant {
  page = 'tab',
  drawer = 'dp_tab',
}

interface UseTabsOptions {
  variant: 'page' | 'drawer';
  onChange?: (tabPath: string) => void;
}

interface UseTabsSettings {
  tabPath: string;
  updateTabPath: (newPath: string) => void;
}

export const useTabs = ({ variant, onChange }: UseTabsOptions): UseTabsSettings => {
  const qsKey = variant === 'page' ? TabVariant.page : TabVariant.drawer;
  const search = useSearch({
    strict: false,
  });

  // Read from useSearch (TanStack Router's state)
  const searchTabPath = String((search as Record<string, string | undefined>)?.[qsKey] || '');

  // Also read directly from URL as source of truth (in case useSearch hasn't updated yet)
  const directUrlTabPath = getUrlQueryParam(qsKey) || '';

  // Prefer direct URL value when it exists (handles race conditions where URL is updated before useSearch reflects it)
  // This ensures we read the correct tab when the drawer opens with a specific tab
  const urlTabPath = directUrlTabPath || searchTabPath;

  const lastUrlTabRef = useRef('');

  // Sync state when URL search param changes
  useEffect(() => {
    if (urlTabPath !== lastUrlTabRef.current) {
      lastUrlTabRef.current = urlTabPath;
      onChange?.(urlTabPath);
    } else if (urlTabPath) {
      lastUrlTabRef.current = urlTabPath;
    }
  }, [urlTabPath, onChange, variant, qsKey]);

  const updateTabPath = useCallback(
    (newPath: string) => {
      // Always check current URL value to avoid stale state issues
      const currentUrlTab = getUrlQueryParam(qsKey) || '';
      if (newPath === urlTabPath && newPath === currentUrlTab) {
        return;
      }
      // Use replace instead of push to avoid adding history entries
      addUrlQueryParam(qsKey, newPath, true);

      onChange?.(newPath);
    },
    [urlTabPath, qsKey, onChange]
  );

  return useMemo(
    () => ({
      tabPath: urlTabPath,
      updateTabPath,
    }),
    [urlTabPath, updateTabPath]
  );
};
